import { useMemo, useContext } from "react"
import { isEmpty } from "lodash"
import { useLastIntradayBatch } from "hooks/intraday"
import { DataStatus, OK } from "helpers/data-statuses"
import { getToday, IsoDate } from "helpers/types/dates"
import { IntradayLastBatch } from "model/state"
import { DashboardContext } from "../context/dashboard"

/**
 * Helper function to calculate intraday mode status
 * @param isIntraday - Base intraday flag
 * @param endDate - Current end date
 * @param intradayDate - Today's date (intraday date)
 * @param lastIntradayBatch - Intraday batch data
 * @param intradayBatchStatus - Status of intraday batch
 */
export function calculateIntradayMode(
  isIntraday: boolean,
  endDate: IsoDate,
  intradayDate: IsoDate,
  lastIntradayBatch: IntradayLastBatch | null,
  intradayBatchStatus: DataStatus,
): [{
  isIntradayMode: boolean
  intradayDataAvailable: boolean
  isEndDateIntraday: boolean
}, DataStatus] {
  const intradayDataAvailable = !isEmpty(lastIntradayBatch) && intradayBatchStatus === OK
  const isEndDateIntraday = endDate === intradayDate
  const isIntradayMode = isIntraday && intradayDataAvailable && isEndDateIntraday

  return [
    {
      isIntradayMode,
      intradayDataAvailable,
      isEndDateIntraday,
    },
    intradayBatchStatus,
  ]
}

/**
 * Hook to get intraday mode status using context values
 */
export function useIntradayMode() {
  const { isIntraday, endDate, instrument, instrumentsMetadata } = useContext(DashboardContext)
  const [lastIntradayBatch, intradayBatchStatus] = useLastIntradayBatch(instrument, instrumentsMetadata.original?.intradayEnabled)
  const today = getToday()

  return useMemo(() => {
    return calculateIntradayMode(isIntraday, endDate, today, lastIntradayBatch, intradayBatchStatus)
  }, [isIntraday, endDate, today, lastIntradayBatch, intradayBatchStatus])
}
